import { StandardTextFieldProps } from '@mui/material';
import React, { useMemo } from 'react';
import { DatePickerInputAdornment } from './DatePickerInputAdornment';
import { EDTextEdit } from '../../TextEdit/EDTextEdit';
import { CommonPropsAndTypes } from '../../_Types/StandardComponentProps';
import { getTryParseDate } from '../Utils/EDDatePickerUtils';
import { TEDFormatDate } from '../../../Utils/Formatters/FormatterClasses/TEDFormatDate';
import { IEditComponentProps } from '../../../Components/Attributes/EditableAttribute/EditComponentTypes';

export type TextEditPropsType = Omit<
  IEditComponentProps<any>,
  'value' | 'onChange' | 'onBlur' | 'classes'
>;
interface IProps extends StandardTextFieldProps {
  editValue: string | undefined;
  editOnChange?: (newValue: string, isValidDate: boolean) => void;
  editOnBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLDivElement>,
    newValue: string,
    isValidDate: boolean
  ) => void;
  editOnFocus?: React.FocusEventHandler<HTMLElement>;
  buttonOnClick?: () => void;
  textEditProps: TextEditPropsType;
  adornmentPadding?: CommonPropsAndTypes.ExpandedSpacingType;
  fullscreenStillRenderTextEdit?: boolean;
  noChangeOnBlur?: boolean;
  noParsing?: boolean;
  parseParams?: string;
  inputGroupRef?: HTMLDivElement | null;
  inputGroupActive?: boolean;
  inputGroupGoNext?: () => void;
}

const dateFormatter = new TEDFormatDate();

export const DatePickerTextEdit: React.FC<
  React.PropsWithChildren<IProps>
> = pickerProps => {
  const editProps = pickerProps.textEditProps;

  const tryParseDate = useMemo(
    () => getTryParseDate(dateFormatter, pickerProps.parseParams),
    [pickerProps.parseParams]
  );

  const disabled = editProps.disabled || editProps.masked;

  return (
    <EDTextEdit
      {...editProps}
      value={pickerProps.editValue ?? ''}
      placeholder="ÅÅÅÅ-MM-DD"
      inputBaseRef={pickerProps.InputProps?.ref || undefined}
      onChange={newValue => {
        if (pickerProps.editOnChange) {
          pickerProps.editOnChange(newValue, Boolean(tryParseDate(newValue)));
        }
      }}
      onFocus={event => {
        if (pickerProps.editOnFocus) {
          pickerProps.editOnFocus(event);
        }
      }}
      onBlur={event => {
        const eventTarget = event.target as
          | HTMLInputElement
          | HTMLDivElement
          | null;
        const eventValue =
          eventTarget && 'value' in eventTarget
            ? eventTarget.value
            : (pickerProps.editValue ?? '');
        const newValue = eventValue;
        const parsedDate =
          pickerProps.noParsing || !newValue ? null : tryParseDate(newValue);
        if (pickerProps.editOnChange && !pickerProps.noChangeOnBlur) {
          pickerProps.editOnChange(
            parsedDate || newValue || '',
            Boolean(parsedDate)
          );
        }
        if (pickerProps.editOnBlur) {
          pickerProps.editOnBlur(
            event,
            parsedDate || newValue || '',
            Boolean(parsedDate)
          );
        }
      }}
      fullscreenStillRenderTextEdit={editProps.fullscreenStillRenderTextEdit}
      endAdornment={
        disabled ? undefined : (
          <DatePickerInputAdornment
            onClick={pickerProps.buttonOnClick}
            tooltip={editProps.tooltip}
            adornmentPadding={editProps.adornmentPadding}
          />
        )
      }
      disableInputGroup
      inputGroupActive={pickerProps.inputGroupActive}
      inputGroupGoNext={pickerProps.inputGroupGoNext}
      inputGroupRef={pickerProps.inputGroupRef}
    />
  );
};

export type { IProps as IDatePickerTextEditProps };
